'use client';

import { useQuery } from '@tanstack/react-query';
import GuestProductCards from './GuestProductCards';
import { getGuestAccessories } from '../../service/getGuestAccessories';
import { useEffect, useState } from 'react';
import type {
  AppliedFilterValues,
  GuestNonScriptRangeProductsProps,
  Product,
  ProductConditionResult,
  ProductFormulationResult,
  ReactSelectOptionProp,
} from '../../interface/interface';
import showToast from '../ToastComponent';
import { getGuestProductBrands } from '../../service/getGuestProductBrands';
import { ProductBrands } from '../../constant/constant';
import { getGuestProductFormulation } from '../../service/getGuestProductFormulation';
import { CloseIcon } from '../../assets/svgImages';
import { getGuestProductConditions } from '../../service/getGuestProductConditions';
import NonScriptFilters from '../NonScriptFilters';
import LoadingWrapper from '../LoadingWrapper';

export function GuestNonScriptRangeProducts(
  props: GuestNonScriptRangeProductsProps,
) {
  const [brands, setBrands] = useState<Array<ReactSelectOptionProp>>([]);
  const [selectedBrand, setSelectedBrand] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const [selectedForm, setSelectedForm] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const [selectedArea, setSelectedArea] = useState<
    Array<ReactSelectOptionProp>
  >([]);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilterValues>({
    brand: [],
    form: [],
    area: [],
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [filterCount, setFilterCount] = useState<number>(0);

  useEffect(() => {
    try {
      const storedBrand = localStorage.getItem('selectedBrand');
      const storedForm = localStorage.getItem('selectedForm');
      const storedArea = localStorage.getItem('selectedArea');

      const brand = storedBrand ? JSON.parse(storedBrand) : [];
      const form = storedForm ? JSON.parse(storedForm) : [];
      const area = storedArea ? JSON.parse(storedArea) : [];

      setSelectedBrand(brand);
      setSelectedForm(form);
      setSelectedArea(area);

      setAppliedFilters({
        brand,
        form,
        area,
      });
    } catch (error) {
      clearAllFilters();
    }
  }, []);

  useEffect(() => {
    const count =
      (selectedBrand?.length > 0 ? 1 : 0) +
      (selectedForm?.length > 0 ? 1 : 0) +
      (selectedArea?.length > 0 ? 1 : 0);

    setFilterCount(count);
  }, [selectedBrand, selectedForm, selectedArea]);

  const {
    data = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ['guest-accessories', appliedFilters],
    queryFn: async () => {
      try {
        const accessories = await getGuestAccessories(
          appliedFilters.brand,
          appliedFilters.form,
          appliedFilters.area,
        );

        if (!accessories) {
          showToast({
            message: 'Unable to fetch accessories!',
            type: 'info',
          });
          return [];
        }

        return accessories;
      } catch (error) {
        console.error('Error fetching guest accessories:', error);
        showToast({
          message: 'Error fetching accessories!',
          type: 'error',
        });
        return [];
      }
    },
    retry: 1,
  });

  const { isLoading: isBrandLoading } = useQuery({
    queryKey: ['guest-productBrand'],
    queryFn: async () => {
      try {
        const result = await getGuestProductBrands();

        if (typeof result === 'number') {
          showToast({
            message: 'Unable to fetch guest product brand!',
            type: 'error',
          });
          return null;
        }

        const filteredBrands = result
          .filter(
            (x: { product_brand: string }) =>
              x.product_brand !== ProductBrands.ATLUS,
          )
          .map((e: { product_brand: string }) => ({
            label: e.product_brand,
            value: e.product_brand,
          }));

        setBrands(filteredBrands);
        return null;
      } catch (error) {
        console.error('Error fetching guest product brands:', error);
        showToast({
          message: 'Error fetching product brands!',
          type: 'error',
        });
        return null;
      }
    },
    retry: 1,
  });

  const { data: formulation, isLoading: isFormLoading } = useQuery({
    queryKey: ['guest-productFormulation'],
    queryFn: async () => {
      const result = await getGuestProductFormulation();

      if (typeof result === 'number') {
        showToast({
          message: 'Unable to fetch guest product formulation!',
          type: 'error',
        });
        return null;
      }
      const filteredFormulation = result
        .filter((e: ProductFormulationResult) =>
          Boolean(e?.productFormulationName),
        )
        .map((e: ProductFormulationResult) => ({
          label: e.productFormulationName,
          value: e.productFormulationName,
        }));

      return filteredFormulation;
    },
  });

  const {
    data: conditions,
    error: isError,
    isLoading: isConditionLoading,
  } = useQuery({
    queryKey: ['guest-productConitions'],
    queryFn: () => {
      return getGuestProductConditions();
    },
  });

  const clearAllFilters = () => {
    setSelectedBrand([]);
    setSelectedForm([]);
    setSelectedArea([]);

    setAppliedFilters({
      brand: [],
      form: [],
      area: [],
    });
  };

  const applyFilters = () => {
    setAppliedFilters({
      brand: selectedBrand,
      form: selectedForm,
      area: selectedArea,
    });

    setMobileFiltersOpen(false);
  };

  useEffect(() => {
    if (error || isError) {
      showToast({
        message: 'Unable to fetch accessories!',
        type: 'info',
      });
    }
  }, [error, isError]);

  const loading =
    isLoading || isFormLoading || isBrandLoading || isConditionLoading;

  return (
    <LoadingWrapper state={loading}>
      <div className="result-block-container">
        <div className="flex justify-end items-center mb-4 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="inline-flex items-center rounded-md bg-primary-blue-500 px-3 py-2 text-sm font-medium text-white shadow-sm"
          >
            ☰ Filters
          </button>
          {filterCount > 0 ? (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ms-3 inline-flex items-center rounded-md bg-primary-blue-500 px-3 py-2 text-sm font-medium text-white shadow-sm"
            >
              {`(${filterCount}) clear`}
            </button>
          ) : (
            <></>
          )}
        </div>

        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-50 lg:hidden overflow-hidden z-[99]">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={() => setMobileFiltersOpen(false)}
            />

            <div className="fixed inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col bg-white rounded-t-2xl shadow-2xl p-6 max-h-[80vh]">
              <div className="overflow-y-auto overscroll-contain touch-pan-y">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-primary-blue-500">
                    Filters
                  </h2>

                  <CloseIcon
                    onClick={() => setMobileFiltersOpen(false)}
                    className="cursor-pointer fill-primary-blue-500 sm:w-4 sm:h-4"
                  />
                </div>

                <NonScriptFilters
                  brands={brands}
                  selectedBrand={selectedBrand}
                  setSelectedBrand={setSelectedBrand}
                  isBrandLoading={isBrandLoading}
                  formulation={formulation}
                  selectedForm={selectedForm}
                  setSelectedForm={setSelectedForm}
                  isFormLoading={isFormLoading}
                  conditions={conditions
                    ?.filter((e: ProductConditionResult) =>
                      Boolean(e?.condition),
                    )
                    ?.map((e: ProductConditionResult) => ({
                      label: e.condition,
                      value: e.condition,
                    }))}
                  selectedArea={selectedArea}
                  setSelectedArea={setSelectedArea}
                  isConditionLoading={isConditionLoading}
                />
                <div className="flex justify-end items-center">
                  <button
                    onClick={applyFilters}
                    className="w-auto text-white p-2 rounded-md bg-primary-blue-500"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="hidden lg:block">
          <NonScriptFilters
            brands={brands}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            isBrandLoading={isBrandLoading}
            formulation={formulation}
            selectedForm={selectedForm}
            setSelectedForm={setSelectedForm}
            isFormLoading={isFormLoading}
            conditions={conditions
              ?.filter((e: ProductConditionResult) => Boolean(e?.condition))
              ?.map((e: ProductConditionResult) => ({
                label: e.condition,
                value: e.condition,
              }))}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            isConditionLoading={isConditionLoading}
            setAppliedFilters={setAppliedFilters}
          />
        </div>

        <ul className="grid grid-cols-5 gap-4 xs:grid-cols-1 sm:grid-cols-2 sm-land:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {data?.length > 0 ? (
            data?.map((product: Product) => (
              <div key={product.id}>
                <GuestProductCards
                  addProductToCart={async (id: string) => {
                    await props.addToCartMutation.mutateAsync(id);
                  }}
                  removeProductFromCart={async (productId: string) => {
                    await props.removeFromCartMutation.mutateAsync(productId);
                  }}
                  product={product}
                  substitueCard={false}
                  isAccessories={true}
                  allowedProduct={false}
                  isGuest={true}
                />
              </div>
            ))
          ) : (
            <p>No Non-Script Range Products Exist</p>
          )}
        </ul>
      </div>
    </LoadingWrapper>
  );
}
